import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';

import { StreamContext } from '@smart-tv/contexts/stream-context';
import { ERROR_CODES } from '@smart-tv/contexts/error-context/error-context';
import useInterval from '@smart-tv/hooks/use-interval';
import { LiveTvChannel, StreamType } from '@smart-tv/typings/content';
import addQueryParams from '@smart-tv/utils/routing/add-query-params';
import encodeProgramSheetParams from '@smart-tv/utils/routing/encode-program-sheet-params';

import { SESSION_STORAGE_SHEET_KEY } from '../program-sheet/_constants/session-storage';
import { usePlayerContext } from './_contexts/player-context';
import { usePlayerEvent } from './_hooks/use-player-event';
import usePlayerEventHandling from './_hooks/use-player-eventhandling';
import formatVisualOnStream from './_utils/format-visual-on-stream';
import getPlayerConfig from './_utils/get-player-config';
import getSubtitleStyles from './_utils/get-subtitle-styles';
import { breadcrumbUrlContainsPathname } from './_utils/navigation-utils';
import {
  BitrateChangedEvent,
  PlayerEvent,
  Props as PlayerProps,
  SeekableRange,
  StreamParams
} from './types';
import useVodPlayer from './vod-wrapper/_hooks/use-vod-player';
import { BaseError, CmdError, networkError } from '@smart-tv/contexts/error-context/error-context';
import { keepStreamAlive } from '@smart-tv/utils/kmm/keepStreamAlive';
import { logErrorEvent } from '@smart-tv/utils/kmm/logErrorEvent';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import { retryButtonText } from '../error-dialog/error-dialog';
import useControls from './_hooks/use-controls';
import { visualOnStreamAtom, openPlayerDevicesAtom } from './atoms';
import { retrievePersistentData } from '@smart-tv/utils/persistent-data/persistent-data';
import { setNetworkSpeed } from '@smart-tv/utils/network-speed';

const KEEP_ALIVE_INTERVAL_MILLIS_DEFAULT = 25000;
const closeButtonText = 'Luk afspiller';

// Used to prevent multiple logErrorEvent calls when
// multiple usePlayer instances are active or the player fires the same error repeatedly.
let lastPlayerErrorEventKey: string | null = null;
let lastPlayerErrorEventTime = 0;
const PLAYER_ERROR_EVENT_OCCURRENCE_WINDOW_MS = 2000;

const usePlayer = (props: PlayerProps) => {
  const { streamParams, children, ...rest } = props;
  const navigate = useNavigate();
  const { handleClose, streamOptions } = useVodPlayer();
  const {
    handleOpenStream,
    handleCloseStream,
    streamType,
    secondsWatched,
    error: streamError,
    setError: setStreamError
  } = useContext(StreamContext);
  const playerControls = usePlayerEventHandling();
  const { openControls } = useControls();

  const { channels, handleInit, handleUnmount, player, settingsOverlay, toggleSettingsOverlay } =
    usePlayerContext();

  const uid = retrievePersistentData<string>('uid');

  const [channel, setChannel] = useState<LiveTvChannel>();
  const [keepAliveIntervalMillis, setKeepAliveIntervalMillis] = useState(
    KEEP_ALIVE_INTERVAL_MILLIS_DEFAULT
  );
  const [openPlayerDevices, setOpenPlayerDevices] = useAtom(openPlayerDevicesAtom);
  const [playerNode, setPlayerNode] = useState<HTMLDivElement | null>(null);
  const [visualOnStream, setVisualOnStream] = useAtom(visualOnStreamAtom);
  const prevSeekableRange = useRef<SeekableRange>();
  const resumeProgress = useRef<number | undefined>(undefined);
  const openPlayerDevicesRef = useRef(openPlayerDevices);
  const { checkNetwork } = useNetwork();

  const [error, setError] = useState<CmdError | BaseError>();
  const [errorSource, setErrorSource] = useState<'openStream' | 'keepAlive' | null>(null);

  useEffect(() => {
    if (streamError && !error) {
      setError(streamError as CmdError | BaseError);
      setErrorSource('openStream');
    }
  }, [streamError, error]);

  useEffect(() => {
    if (error && error.offline && settingsOverlay) {
      toggleSettingsOverlay();
    }
  }, [error, settingsOverlay, toggleSettingsOverlay]);

  const getCurrentProgress = useCallback((seekableRange: SeekableRange, streamType: StreamType) => {
    if (streamType === StreamType.LiveStream) return;

    const { position, range } = seekableRange;

    return Math.floor(position - range.start);
  }, []);

  useEffect(() => {
    if (!channels || !streamParams?.sid) return;

    const selectedChannel = channels.find((channel) => channel.sid === streamParams.sid);

    setChannel((prevState) =>
      selectedChannel?.sid !== prevState?.sid ||
      selectedChannel?.currentProgram?.eventId !== prevState?.currentProgram?.eventId
        ? selectedChannel
        : prevState
    );
  }, [channels, streamParams?.sid]);

  const openStream = useCallback(
    async (streamParams: StreamParams) => {
      try {
        const streamResponse = await handleOpenStream(streamParams);

        if (!streamResponse) return;

        const data = streamResponse;

        const redirectedStream = await window.fetch(data.streamUrl, {
          redirect: 'follow',
          method: 'HEAD'
        });

        const visualOnStream = formatVisualOnStream(data, redirectedStream.url);

        // Set the keep alive interval from the stream data.
        setKeepAliveIntervalMillis(data.keepAliveIntervalInSecs * 1000);
        setVisualOnStream(visualOnStream);
      } catch (error) {
        if ((error as CmdError).code === ERROR_CODES.MAX_DEVICES) {
          setOpenPlayerDevices(true); // Update state
          openPlayerDevicesRef.current = true; // Update ref
          return;
        } else if (error === networkError || (error as BaseError).offline) {
          setError(networkError);
          setErrorSource('openStream');
        } else {
          setError(error as CmdError);
          setErrorSource('openStream');
        }
      }
    },
    [handleOpenStream, setError, setVisualOnStream, setOpenPlayerDevices]
  );

  const closeStream = useCallback(
    async (streamParams: StreamParams) => {
      // remove old stream completely as there is a risk that the old stream will briefly be shown when the next loads
      setVisualOnStream(undefined);

      const progress =
        prevSeekableRange.current && streamType
          ? getCurrentProgress(prevSeekableRange.current, streamType)
          : undefined;

      try {
        await handleCloseStream({
          ...streamParams,
          ...(progress !== undefined && progress !== null && progress !== 0 && { progress })
        });
      } catch (error) {
        console.error('[usePlayer] Error closing stream:', error);
      }
      prevSeekableRange.current = undefined;
    },
    [streamType, getCurrentProgress, handleCloseStream, setVisualOnStream]
  );

  const handlePlayerOpened = useCallback(() => {
    if (!player) return;

    if (process.env.LOG_PLAYEREVENTS === 'true') {
      const qualityLevels = player.getQualityLevels(); // which resolutions are available
      const summary = qualityLevels
        .map((level) => `${level.id}:${level.width}x${level.height}@${level.bandwidth}`)
        .join(' | ');

      console.log(`[QualityLevels] ${summary}`);
      if (console.table) {
        console.table(qualityLevels);
      } else {
        console.log('[QualityLevels]', qualityLevels);
      }
    }

    const subtitleStyles = getSubtitleStyles();

    player.setSubtitleStyles(subtitleStyles);
  }, [player]);

  const handlePlaytimeUpdated = useCallback((seekableRange: SeekableRange) => {
    prevSeekableRange.current = seekableRange;
  }, []);

  const handleRefreshStream = useCallback(
    async (streamParams?: StreamParams) => {
      if (!streamParams) return;

      await closeStream(streamParams);

      await openStream(streamParams);
    },
    [closeStream, openStream]
  );

  const handleKeepAlive = useCallback(() => {
    if (
      !streamParams ||
      !prevSeekableRange.current ||
      !streamType ||
      openPlayerDevicesRef.current
    ) {
      return;
    }

    const progress = getCurrentProgress(prevSeekableRange.current, streamType);

    keepStreamAlive({
      ...streamParams,
      ...(progress !== undefined && progress !== null && { progress })
    }).catch((error: any) => {
      const isPaused = player ? player.isPaused() : undefined;
      if ((error === networkError || (error as BaseError).offline) && !isPaused) {
        player?.pause();
        setError(networkError);
        setErrorSource('keepAlive');
      }
    });
  }, [streamParams, streamType, getCurrentProgress, player]);

  useEffect(() => {
    openPlayerDevicesRef.current = openPlayerDevices;
  }, [openPlayerDevices]);

  // Init the player on mount
  useEffect(() => {
    if (player || !playerNode) return;

    const voPlayer = new window.voPlayer.Player(playerNode);

    voPlayer.addPlugin(window.voPlayer.voSubtitlesPlugin);
    voPlayer.addPlugin(window.voPlayer.voFCCStylePlugin);
    voPlayer.addPlugin(window.voPlayer.voAnalyticsPlugin);

    const userEmail = retrievePersistentData<string>('last-login-email');

    const playerConfig = getPlayerConfig(userEmail ?? '', uid ?? '');

    voPlayer.init(playerConfig);

    handleInit(voPlayer, playerNode);
  }, [uid, handleInit, playerNode, player]);

  // Destroy the player on unmount
  useLayoutEffect(() => {
    return () => handleUnmount();
  }, [handleUnmount]);

  useEffect(() => {
    if (!visualOnStream || !player || !playerNode) return;

    const progressToUse = resumeProgress.current ?? streamParams?.progress;

    if (streamType === StreamType.StartOver) {
      const startOverPosition = progressToUse ?? secondsWatched ?? 0;
      player.setPosition(startOverPosition);
    } else if (progressToUse !== undefined && progressToUse !== null) {
      player.setPosition(progressToUse);
      resumeProgress.current = undefined;
    }

    player.open(visualOnStream);

    return () => {
      player.close();
    };
  }, [visualOnStream, player, playerNode, streamParams?.progress, streamType, secondsWatched]);

  // The interval is used to keep the player alive, while the stream is playing.
  useInterval(handleKeepAlive, keepAliveIntervalMillis);

  const closePlayer = useCallback(
    (returningToSheet?: boolean) => {
      handleClose?.(returningToSheet);
    },
    [handleClose]
  );

  const handlePlayerRoutingOnClose = useCallback(() => {
    const programSheetRef = sessionStorage.getItem(SESSION_STORAGE_SHEET_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_SHEET_KEY);
    let returningToSheet = false;
    if (programSheetRef) {
      const { sid, eventId, contentId, contentType, subProviderId } = JSON.parse(programSheetRef);
      if (sid && eventId) {
        const url = addQueryParams({
          programsheet: encodeProgramSheetParams(sid, eventId, undefined, undefined, subProviderId)
        });

        const urlFormatted = url.replace(/\/live-tv\/\d+(\?)/, '$1');

        navigate(urlFormatted);
        returningToSheet = true;
      } else if (contentId && contentType) {
        navigate(
          addQueryParams({
            programsheet: encodeProgramSheetParams(
              undefined,
              undefined,
              contentId,
              contentType,
              subProviderId
            )
          })
        );
        returningToSheet = true;
      }
    } else {
      const navigatedFromTvGuide = breadcrumbUrlContainsPathname('tv-guide');
      const navigatedFromUniverse = breadcrumbUrlContainsPathname('universe');
      const navigatedFromNPVR = breadcrumbUrlContainsPathname('settings');
      const navigatedFromSearch = breadcrumbUrlContainsPathname('search');

      if (navigatedFromTvGuide) {
        navigate('/tv-guide');
      } else if (navigatedFromUniverse) {
        const currentUrl = window.location.pathname;
        const urlWithoutLiveTv = currentUrl.replace(/\/live-tv\/\d+/, '');
        navigate(urlWithoutLiveTv);
      } else if (navigatedFromNPVR) {
        //do nothing for now, as we are already in the NPVR section
      } else if (navigatedFromSearch) {
        navigate('/search');
      } else {
        navigate('/');
      }
    }

    closePlayer(returningToSheet);
  }, [closePlayer, navigate]);

  const handlePlayerError = useCallback(
    (errorEvent: any) => {
      if (errorEvent && errorEvent.type === PlayerEvent.Error) {
        if (error?.offline) {
          return;
        }

        const errorKey = `${errorEvent.code}:${errorEvent.message}`;
        const now = Date.now();
        if (
          errorKey !== lastPlayerErrorEventKey ||
          now - lastPlayerErrorEventTime > PLAYER_ERROR_EVENT_OCCURRENCE_WINDOW_MS
        ) {
          lastPlayerErrorEventKey = errorKey;
          lastPlayerErrorEventTime = now;
          void logErrorEvent({ errorCode: errorEvent.code, logMessage: errorEvent.message });
        }
      } else {
        console.log('Not a valid player error, skipping');
      }
    },
    [error]
  );

  const handleLogAdded = useCallback((logEvent: any) => {
    if (process.env.LOG_PLAYEREVENTS === 'true') {
      console.log('[VisualOn Debug]', logEvent);
    }
  }, []);

  const handleBitrateChanged = useCallback((event: BitrateChangedEvent) => {
    if (event?.mediaType !== 'video' || !event.bandwidth) return;

    setNetworkSpeed(event.bandwidth);

    if (process.env.LOG_PLAYEREVENTS === 'true') {
      console.log(
        `[VisualOn Bitrate] ${event.bandwidth} bps (${event.width}x${event.height}) ${event.oldQuality} -> ${event.newQuality}`
      );
    }
  }, []);

  const clearErrors = useCallback(() => {
    setError(undefined);
    setErrorSource(null);
    setStreamError(null);
  }, [setError, setStreamError]);

  const handleResumeStream = () => {
    void (async () => {
      const isOnline = await checkNetwork();
      if (!isOnline) {
        return;
      }

      clearErrors();

      if (errorSource === 'openStream' && !streamParams && streamOptions?.streamParams) {
        openStream(streamOptions.streamParams); // if the stream never initialized streamparams will be undefined
      } else if (errorSource === 'openStream') {
        handleRefreshStream(streamParams);
      } else {
        player?.play();
        // reset auto hide timeout on controls
        openControls();
      }
    })();
  };

  const handleReloadStream = useCallback(() => {
    clearErrors();
    handleRefreshStream(streamParams);
  }, [handleRefreshStream, clearErrors, streamParams]);

  const handleClosePlayer = useCallback(() => {
    clearErrors();
    handlePlayerRoutingOnClose();
  }, [handlePlayerRoutingOnClose, clearErrors]);

  const errorDialogProps = error?.convertErrorToErrorProps(
    { label: retryButtonText, onClick: handleResumeStream },
    { label: closeButtonText, onClick: handleClosePlayer }
  );

  usePlayerEvent(PlayerEvent.Opened, handlePlayerOpened);
  usePlayerEvent(PlayerEvent.PlayTimeUpdated, handlePlaytimeUpdated);
  usePlayerEvent(PlayerEvent.Error, handlePlayerError);
  usePlayerEvent(PlayerEvent.LogAdded, handleLogAdded);
  usePlayerEvent(PlayerEvent.BitrateChanged, handleBitrateChanged);

  return {
    channel,
    handlePlayerRoutingOnClose,
    setChannel,
    handleRefreshStream,
    handlePlayerError,
    setPlayerNode,
    openPlayerDevices,
    setOpenPlayerDevices,
    playerControls,
    children,
    closeVod: closePlayer,
    closeStream,
    error,
    setError,
    handleResumeStream,
    handleReloadStream,
    handleClosePlayer,
    errorDialogProps,
    openStream,
    ...rest
  };
};

export default usePlayer;
